import "../styles/base.css";
import "../styles/globals.css";
import "../styles/form.css";
import "../styles/stripe.css";
import debug from "debug";
import { ReactElement, ReactNode } from "react";
import type { AppProps } from "next/app";
import { Provider } from "react-redux";
import { useStore } from "@/lib/redux/store";
import { keyboardEventCodePolyfill } from "@/lib/polyfills/keyboardEventCodePolyfill";
import { intlNumberFormatPolyfill } from "@/lib/polyfills/intlNumberFormatPolyfill";
import { EnsurePolyfilled } from "@/lib/build/EnsurePolyfilled";
import { init } from "@/lib/build/sentry";
import { FormConfig } from "@evergiving/common/dist/es2015/form";

interface CustomAppProps extends AppProps {
  pageProps: {
    formConfig?: FormConfig;
  };
}

const log = debug(__filename);

init();

function WithStore({
  formConfig,
  children,
}: {
  formConfig: FormConfig;
  children: ReactNode;
}): ReactElement {
  const store = useStore(formConfig);
  return <Provider store={store}>{children}</Provider>;
}

function DigitalFormApp({
  Component,
  pageProps,
  err,
}: CustomAppProps & { err?: Error }): ReactElement {
  log(`NEXT_BUILD_ID=${String(process.env.NEXT_BUILD_ID)}`);
  log(`NODE_ENV=${String(process.env.NODE_ENV)}`);
  log("Form configuration used:", pageProps.formConfig);

  const polyfills = [keyboardEventCodePolyfill, intlNumberFormatPolyfill];
  const { formConfig } = pageProps;

  return (
    <EnsurePolyfilled
      locale={pageProps.formConfig?.locale ?? "en"}
      polyfills={polyfills}
    >
      {formConfig ? (
        <WithStore formConfig={formConfig}>
          <Component {...pageProps} err={err} />
        </WithStore>
      ) : (
        <Component {...pageProps} err={err} />
      )}
    </EnsurePolyfilled>
  );
}

export default DigitalFormApp;
